import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { FileText, User, Clock, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SystemLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/audit/logs?limit=200`);
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching logs:", error);
      toast.error("Failed to fetch audit logs");
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (actionType) => {
    switch (actionType.toLowerCase()) {
      case "edit":
        return "bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30";
      case "cancel":
        return "bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30";
      case "seat":
      case "seated":
        return "bg-[#22C55E]/20 text-[#22C55E] border-[#22C55E]/30";
      case "status change":
        return "bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30";
      default:
        return "bg-[#A1A1AA]/20 text-[#A1A1AA] border-[#A1A1AA]/30";
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      return format(new Date(timestamp), "MMM d, yyyy HH:mm:ss");
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#09090B]">
      {/* Header */}
      <div className="border-b border-[#27272A] bg-[#18181B]/80 backdrop-blur-md">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight font-mono flex items-center gap-3">
                <FileText className="w-8 h-8 text-[#22C55E]" />
                System Logs
              </h1>
              <p className="text-sm text-[#A1A1AA] mt-1">Audit trail of all staff actions</p>
            </div>
            <div className="glass-card px-4 py-2 rounded-lg">
              <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Total Logs</p>
              <p className="text-2xl font-bold font-mono text-[#22C55E]">{logs.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="flex-1 overflow-auto scroll-container p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#22C55E]"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 glass-card rounded-lg">
            <AlertCircle className="w-16 h-16 text-[#27272A] mb-4" />
            <p className="text-lg text-[#A1A1AA]">No audit logs found</p>
            <p className="text-sm text-[#A1A1AA] mt-2">Staff actions will appear here</p>
          </div>
        ) : (
          <div className="glass-card rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#09090B] border-b border-[#27272A] sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#A1A1AA] uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#A1A1AA] uppercase tracking-wider">
                    Staff
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#A1A1AA] uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#A1A1AA] uppercase tracking-wider">
                    Reservation
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#A1A1AA] uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr
                    key={log.id}
                    className="border-b border-[#27272A]/50 hover:bg-[#27272A]/30 transition-colors"
                  >
                    {/* Timestamp */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#A1A1AA]" />
                        <span className="text-sm font-mono text-[#FAFAFA]">
                          {formatTimestamp(log.created_at)}
                        </span>
                      </div>
                    </td>

                    {/* Staff */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-[#22C55E]" />
                        <div>
                          <p className="font-semibold text-[#FAFAFA]">{log.staff_name}</p>
                          <p className="text-xs text-[#A1A1AA] font-mono">Code: {log.staff_code}</p>
                        </div>
                      </div>
                    </td>

                    {/* Action Type */}
                    <td className="px-4 py-3">
                      <Badge className={`${getActionColor(log.action_type)} font-mono`}>
                        {log.action_type}
                      </Badge>
                    </td>

                    {/* Reservation Ref */}
                    <td className="px-4 py-3">
                      {log.reservation_ref ? (
                        <span className="text-sm font-mono text-[#3B82F6]">#{log.reservation_ref}</span>
                      ) : (
                        <span className="text-sm text-[#A1A1AA]">-</span>
                      )}
                    </td>

                    {/* Details */}
                    <td className="px-4 py-3">
                      <p className="text-sm text-[#A1A1AA] max-w-md truncate">
                        {log.details || "-"}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemLogs;
